import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { BreakthroughTips } from '../components/BreakthroughTip';
import { getTipsForContext } from '../lib/breakthrough';

interface Theme {
  id: string;
  name: string;
  description: string;
}

interface EventDetail {
  eventId: string;
  name: string;
  themes: Theme[];
}

export function TeamCreatePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [themeId, setThemeId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Don't auto-select a theme — teams should pick deliberately. They can
    // also skip and set it later from the submission editor.
    networkManager
      .get<{ status: boolean; data: EventDetail }>(`/api/events/${eventId}`)
      .then((res) => setEvent(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [eventId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const validationErrors: Record<string, string> = {};
    if (!name.trim()) {
      validationErrors.name = 'Team name is required.';
    }
    // Theme is optional at creation — it can be set later from the submission editor.
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const res = await networkManager.post<{ status: boolean; data: { teamId: string } }>(
        `/api/events/${eventId}/teams`,
        { name: name.trim(), themeId: themeId || null },
      );
      navigate(`/events/${eventId}/teams/${res.data.teamId}`);
    } catch (err: any) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        setErrors(apiErrors);
      } else {
        const message = err?.response?.data?.message || 'Failed to create team. Please try again.';
        setErrors({ form: message });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!event) {
    return <p className="py-12 text-center text-ink-secondary">Event not found.</p>;
  }

  return (
    <div className="animate-fade-in">
      <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Back to Event
      </Link>

      <h1 className="font-display text-3xl font-bold text-ink">Create a Team</h1>
      <p className="mt-1 text-ink-secondary">
        Start a new team for <span className="font-medium text-ink">{event.name}</span>
      </p>

      {errors.form && (
        <div className="mt-4 card border-red-300 bg-red-50 p-4 text-sm text-red-700">
          {errors.form}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 max-w-lg space-y-5">
        {/* Team Name */}
        <div>
          <label htmlFor="team-name" className="block text-sm font-medium text-ink">
            Team Name
          </label>
          <input
            id="team-name"
            type="text"
            className={`input-base mt-1 w-full ${errors.name ? 'border-red-400' : ''}`}
            placeholder="Enter your team name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Theme — optional at creation. Defaults to "No theme yet" so nobody
            accidentally picks the first option by inattention. Teams can
            change it any time from the submission editor. */}
        <div>
          <label htmlFor="theme" className="block text-sm font-medium text-ink">
            Theme <span className="text-ink-tertiary font-normal">(optional)</span>
          </label>
          <select
            id="theme"
            className={`input-base mt-1 w-full ${errors.themeId ? 'border-red-400' : ''}`}
            value={themeId}
            onChange={(e) => setThemeId(e.target.value)}
            disabled={submitting}
          >
            <option value="">No theme yet — I&apos;ll decide later</option>
            {event.themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
          {errors.themeId && <p className="mt-1 text-sm text-red-600">{errors.themeId}</p>}
          {themeId && event.themes.find((t) => t.id === themeId)?.description && (
            <p className="mt-1 text-sm text-ink-tertiary">
              {event.themes.find((t) => t.id === themeId)?.description}
            </p>
          )}
          {!themeId && (
            <p className="mt-1 text-sm text-ink-tertiary">
              You can pick one later from the submission editor.
            </p>
          )}
        </div>

        {/* Submit */}
        <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={submitting}>
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Create Team
        </button>
      </form>

      <div className="mt-6">
        <BreakthroughTips tips={getTipsForContext('team-formation')} title="Team Formation Tips" />
      </div>
    </div>
  );
}
